// Sales, Zkušební tréninky (mockup v8, obrazovka 8).
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ArrowUpRight, CalendarPlus, Info } from "lucide-react";
import { DlazdiceFiltr, RozbalovaciVyber, EmptyState } from "../../components/ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { salesGet, denKratce, casBezNuly } from "../../components/sales/salesApi.js";
import { Pil, PruhSkupiny, RadekKontaktu, KartaObal, Oznameni } from "../../components/sales/salesUi.jsx";
import { ZkusebniDetailDialog, PresunDialog, ZrusitDialog } from "../../components/sales/ZkusebniDialogy.jsx";

const POHLEDY = [
  { key: "nadchazejici", label: "Nadcházející" }, { key: "drzene", label: "Drží se na webu" }, { key: "bez_faktury", label: "Bez faktury" }, { key: "ceka", label: "Čeká na platbu" }, { key: "po_splatnosti", label: "Po splatnosti" },
  { key: "zaplaceno", label: "Zaplaceno" }, { key: "probehle", label: "Proběhlé" }, { key: "zrusene", label: "Zrušené" },
];
const krestni = (j) => String(j || "").split(" ")[0];

// Kolik minut zbývá do vypršení držení z Trial automatu. Po vypršení termín
// uvolní cron a řádek ze seznamu zmizí.
function zbyvaMinut(doKdy) {
  if (!doKdy) return null;
  const min = Math.round((new Date(doKdy).getTime() - Date.now()) / 60000);
  return min > 0 ? min : 0;
}
function OdznakDrzeni({ z, mala }) {
  const [, tik] = useState(0);
  useEffect(() => { const t = setInterval(() => tik((n) => n + 1), 20000); return () => clearInterval(t); }, []);
  const min = zbyvaMinut(z.drzeno_do);
  return <Pil ton="pozor" mala={mala} casti={["Trial automat", min == null ? "drží se" : min > 0 ? `zbývá ${min} min` : "vyprší"]} />;
}

export default function SalesZkusebni() {
  const uzka = useUzkaObrazovka();
  const navigate = useNavigate();
  const { nactiDnes } = useOutletContext();
  const [pohled, setPohled] = useState("nadchazejici");
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [vybrany, setVybrany] = useState(null);
  const [dialog, setDialog] = useState(null);

  const nacti = useCallback(async () => {
    try { setData(await salesGet("zkusebni", { pohled })); setChyba(""); } catch (e) { setChyba(e.message); }
  }, [pohled]);
  useEffect(() => { setData(null); nacti(); }, [nacti]);
  useEffect(() => { const h = () => nacti(); window.addEventListener("fw40-sales-obnovit", h); return () => window.removeEventListener("fw40-sales-obnovit", h); }, [nacti]);

  const otevri = (z) => { setVybrany(z); setDialog("detail"); };
  const poZmene = () => { nactiDnes?.(); return nacti(); };
  const p = data?.pocty || {};
  const seznam = data?.seznam || [];
  const nazev = POHLEDY.find((x) => x.key === pohled)?.label;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <DlazdiceFiltr popisek="Nadcházející" hodnota={p.nadchazejici ?? "…"} ton="znacka" zapnuto={pohled === "nadchazejici"} onClick={() => setPohled("nadchazejici")} />
        <DlazdiceFiltr popisek="Čeká na platbu" hodnota={p.ceka ?? "…"} ton="pozor" zapnuto={pohled === "ceka"} onClick={() => setPohled("ceka")} />
        <DlazdiceFiltr popisek="Po splatnosti" hodnota={p.po_splatnosti ?? "…"} ton="problem" zapnuto={pohled === "po_splatnosti"} onClick={() => setPohled("po_splatnosti")} />
        <DlazdiceFiltr popisek="Zaplaceno" hodnota={p.zaplaceno ?? "…"} ton="ok" zapnuto={pohled === "zaplaceno"} onClick={() => setPohled("zaplaceno")} />
        {p.drzene > 0 && (
          <DlazdiceFiltr popisek="Drží se na webu" hodnota={p.drzene} ton="pozor" zapnuto={pohled === "drzene"} onClick={() => setPohled("drzene")} />
        )}
        {p.bez_faktury > 0 && (
          <DlazdiceFiltr popisek="Bez faktury" hodnota={p.bez_faktury} ton="problem" zapnuto={pohled === "bez_faktury"} onClick={() => setPohled("bez_faktury")} />
        )}
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <RozbalovaciVyber stitek="Zobrazit" items={POHLEDY} value={pohled} onChange={setPohled} aria-label="Zobrazit" />
      </div>
      {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
      {!data ? <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-brand-light" />)}</div>
        : !seznam.length ? <KartaObal><EmptyState icon={CalendarPlus} title="Žádný zkušební trénink" hint={`V pohledu ${nazev} teď nic není.`} /></KartaObal>
          : uzka ? (
            <KartaObal>
              <PruhSkupiny title={nazev} pocet={seznam.length} />
              {seznam.map((z) => (
                <RadekKontaktu key={z.id} jmeno={z.jmeno} onOtevrit={() => otevri(z)}
                  pilulky={<><Pil ton="znacka" casti={[denKratce(z.datum), casBezNuly(z.cas), krestni(z.trener)]} /><Pil ton={z.platba.ton} casti={z.platba.text} />{z.zdroj_text && <Pil ton="neutralni" casti={z.zdroj_text} />}{z.drzene && <OdznakDrzeni z={z} />}</>} />
              ))}
            </KartaObal>
          ) : (
            <KartaObal>
              <div className="p-3 sm:p-4"><div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-3 py-2">Termín</th><th className="px-3 py-2">Zájemkyně</th><th className="px-3 py-2">Trenér</th><th className="px-3 py-2">Faktura</th><th className="px-3 py-2">Zdroj</th><th className="px-3 py-2">Pravidelně</th><th className="px-3 py-2" /></tr></thead>
                  <tbody>
                    {seznam.map((z) => (
                      <tr key={z.id} className="border-t border-slate-100">
                        <td className="whitespace-nowrap px-3 py-2.5"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{denKratce(z.datum)}</div><div className="font-bold tabular-nums text-slate-900">{casBezNuly(z.cas)}</div></td>
                        <td className="px-3 py-2.5 font-bold text-brand-deep">{z.jmeno}</td>
                        <td className="px-3 py-2.5">{z.trener}</td>
                        <td className="px-3 py-2.5"><div className="flex flex-wrap items-center gap-1.5"><Pil ton={z.platba.ton} casti={z.platba.text} mala />{z.drzene && <OdznakDrzeni z={z} mala />}</div></td>
                        <td className="px-3 py-2.5"><Pil ton={z.zdroj_text === "sales" ? "znacka" : "neutralni"} casti={z.zdroj_text || "sales"} mala /></td>
                        <td className="px-3 py-2.5 text-slate-600">{[z.pravidelne, z.delka].filter(Boolean).join(", ")}</td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap"><span className="inline-flex items-center gap-1">
                          {z.stav === "aktivni" && !z.probehl && <button type="button" className="btn-secondary !min-h-0 px-3 py-1.5 text-xs" onClick={() => { setVybrany(z); setDialog("presun"); }}>Přesunout</button>}
                          <button type="button" className="btn-ikona" onClick={() => otevri(z)} aria-label={`Otevřít ${z.jmeno}`} title="Otevřít"><ArrowUpRight className="h-5 w-5" /></button>
                        </span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            </KartaObal>
          )}
      <div className="flex items-start gap-2 text-[13px] leading-snug text-slate-500"><Info className="mt-0.5 h-4 w-4 shrink-0" /><span>Platby se kontrolují ve FAPI samy, zhruba každou hodinu a při otevření téhle stránky.</span></div>

      <ZkusebniDetailDialog z={vybrany} open={dialog === "detail"} onClose={() => setDialog(null)}
        onPresun={() => setDialog("presun")} onZrusit={() => setDialog("zrusit")} onKarta={() => navigate(`/sales/kontakt/${vybrany.lead_id}`)} />
      <PresunDialog z={vybrany} open={dialog === "presun"} onClose={() => setDialog(null)} onHotovo={poZmene} />
      <ZrusitDialog z={vybrany} open={dialog === "zrusit"} onClose={() => setDialog(null)} onHotovo={poZmene} />
    </div>
  );
}
